import React from 'react';
import { useAppSelector } from '../../store/hooks';
import { CText, SEO } from '../../components/common';
import { FiMail, FiGlobe, FiPhone } from 'react-icons/fi';

const AboutApp: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);

  return (
    <div
      className="min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO
        title="About Yantrai - AI-Powered Investment Platform for India"
        description="Yantrai is an all-in-one investment platform with AI-powered portfolio analysis, SEBI-registered expert consultations, and real-time market data for Indian investors."
        canonical="/profile/about"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Yantrai',
          url: 'https://yantrai.com',
          logo: 'https://yantrai.com/logos/logo-tech.svg',
          description: 'AI-powered investment platform for Indian investors with portfolio analysis, SEBI-registered expert consultations, and real-time market data.',
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'support@yantrai.com',
            contactType: 'customer service',
          },
        }}
      />
      {/* Gradient Header */}
      <div
        className="p-6 lg:p-8 pb-12"
        style={{
          background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 100%)`,
        }}
      >
        <button
          onClick={() => window.history.back()}
          className="mb-4 text-white opacity-80 hover:opacity-100"
        >
          ← Back
        </button>

        <div className="text-center">
          <img
            src="/logos/logo-tech.svg"
            alt="Yantrai"
            className="w-20 h-20 rounded-2xl mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold m-0" style={{ color: colors.white }}>
            Yantrai
          </h1>
          <p className="text-sm m-0 mt-1" style={{ color: colors.white, opacity: 0.8 }}>
            AI-Powered Investment Platform
          </p>
          <p className="text-xs m-0 mt-2" style={{ color: colors.white, opacity: 0.6 }}>
            Version 1.0.0
          </p>
        </div>
      </div>

      <div className="p-4 lg:p-8 -mt-6">
        <div className="lg:max-w-3xl lg:mx-auto">
          {/* About Us Card */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.inputBackground }}
          >
            <CText type="B18" color={colors.textColor} style={{ marginBottom: 12 }}>
              About Us
            </CText>
            <CText type="r14" color={colors.grayScale500} style={{ lineHeight: 1.6, marginBottom: 12 }}>
              Yantrai is an AI-powered investment platform built for Indian investors. Analyze your portfolio health, get AI stock verdicts, and make data-driven decisions — all in one place.
            </CText>
            <CText type="r14" color={colors.grayScale500} style={{ lineHeight: 1.6 }}>
              Our mission is to make investing accessible to everyone by providing a simple, secure, and transparent platform. We connect you with SEBI-registered experts who can guide you through your investment journey.
            </CText>
          </div>

          {/* Features Card */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.inputBackground }}
          >
            <CText type="B18" color={colors.textColor} style={{ marginBottom: 12 }}>
              Key Features
            </CText>
            <ul className="space-y-3">
              {[
                'Multi-asset investments (Crypto, Stocks, Gold, NFTs)',
                'Expert consultations with SEBI-registered advisors',
                'Real-time market data and price alerts',
                'Secure transactions with bank-grade encryption',
                'Easy portfolio tracking and analytics',
              ].map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span style={{ color: colors.primary }}>•</span>
                  <CText type="r14" color={colors.grayScale500}>
                    {feature}
                  </CText>
                </li>
              ))}
            </ul>
          </div>

          {/* Join Our Team Card */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{
              background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 100%)`,
            }}
          >
            <CText type="B18" color={colors.white} style={{ marginBottom: 8 }}>
              Join Our Team
            </CText>
            <CText type="r14" color={colors.white} style={{ opacity: 0.8, marginBottom: 12 }}>
              We're always looking for talented individuals to join our growing team.
            </CText>
            <button
              className="px-4 py-2 rounded-lg"
              style={{ backgroundColor: colors.white }}
            >
              <CText type="M14" color={colors.primary}>
                View Careers
              </CText>
            </button>
          </div>

          {/* Contact Info */}
          <div
            className="rounded-2xl p-6"
            style={{ backgroundColor: colors.inputBackground }}
          >
            <CText type="B18" color={colors.textColor} style={{ marginBottom: 12 }}>
              Contact Us
            </CText>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {React.createElement(FiMail as any, { size: 18, color: colors.primary })}
                <CText type="r14" color={colors.grayScale500}>
                  support@yantrai.com
                </CText>
              </div>
              <div className="flex items-center gap-3">
                {React.createElement(FiGlobe as any, { size: 18, color: colors.primary })}
                <CText type="r14" color={colors.grayScale500}>
                  www.yantrai.com
                </CText>
              </div>
              <div className="flex items-center gap-3">
                {React.createElement(FiPhone as any, { size: 18, color: colors.primary })}
                <CText type="r14" color={colors.grayScale500}>
                  +91 1800-XXX-XXXX
                </CText>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <CText type="r12" color={colors.grayScale500}>
              © 2026 Yantrai. All rights reserved.
            </CText>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutApp;
